import React from "react";
import { Analytics } from "../../Analytics";
import { STRINGS } from "../../strings";
import { delay } from "../../system/delay";
import { translate } from "../../translate";
import { setStatusBarAsOverlayWhite } from "../../utils/statusBar";
import { PIN_LENGTH } from "../PinInput";
import ForgotPinModal from "./components/ForgotPinModal";
import InvalidPinModal from "./components/InvalidPinModal";
import PinUnlockScreen from "./PinUnlockScreen";
import usePinUnlockVisibility from "./usePinUnlockVisibility";

interface Props {
  correctPinCode: string;
  onUnlocked: () => void;
  onForgotPinClick: () => void;
  onInvalidPinCodeEntered: () => void;
}

const PinUnlockActivity = (props: Props) => {
  const [pinValue, setPinValue] = React.useState("");
  const [error, setError] = React.useState<string>();
  const [isForgotPinModalOpen, setForgotPinModalOpen] = React.useState(false);
  const [isPinCodeLoading, setIsPinCodeLoading] = React.useState(false);
  const [, setPinUnlockVisibility] = usePinUnlockVisibility();

  React.useEffect(() => {
    setPinUnlockVisibility(true);
  }, [setPinUnlockVisibility]);

  React.useEffect(() => {
    setTimeout(setStatusBarAsOverlayWhite, 0);
  }, []);

  React.useEffect(() => {
    try {
      if (document.activeElement !== null && document.activeElement.tagName !== "BODY") {
        console.log("blurring active element");
        (document.activeElement as HTMLElement).blur();
      }
    } catch (e) {
      console.error("failed to blur active element", e);
    }
  }, []);

  const handleChangePin = React.useCallback(
    async (val: string) => {
      setError(undefined);
      setPinValue(val);

      if (val.length !== PIN_LENGTH) {
        return;
      }

      // I'm delaying the action so the user will be able to see the fourth digit visually
      await delay(250);

      if (val !== props.correctPinCode) {
        props.onInvalidPinCodeEntered.call(null);
        setError(translate(STRINGS.INCORRECT_PIN_CODE_PLEASE_TRY_AGAIN));
        setPinValue("");
        return;
      }

      props.onUnlocked.call(null);
      setPinUnlockVisibility(false);

      return;
    },
    [props.correctPinCode, props.onInvalidPinCodeEntered, props.onUnlocked, setPinUnlockVisibility]
  );

  const handleClickForgotPin = React.useCallback(() => {
    setForgotPinModalOpen(true);
    Analytics.track("Forgot Pin Code Clicked");
  }, []);

  const handleClickCancelForgotPin = React.useCallback(() => {
    setForgotPinModalOpen(false);
  }, []);

  const handleRequestCloseInvalidModal = React.useCallback(() => {
    setError(undefined);
  }, []);

  const handleClickSignOut = React.useCallback(() => {
    setIsPinCodeLoading(true);
    Analytics.track("Forgot Pin Code Activated");

    props.onForgotPinClick.call(null);
  }, [props.onForgotPinClick]);

  return (
    <>
      <PinUnlockScreen
        pinValue={pinValue}
        onChangePin={handleChangePin}
        onClickForgotPin={handleClickForgotPin}
      />
      <ForgotPinModal
        isOpen={isForgotPinModalOpen}
        isLoading={isPinCodeLoading}
        onClickSignOut={handleClickSignOut}
        onRequestClose={handleClickCancelForgotPin}
      />
      <InvalidPinModal
        error={error}
        isLoading={isPinCodeLoading}
        onRequestClose={handleRequestCloseInvalidModal}
      />
    </>
  );
};

export default React.memo(PinUnlockActivity);
